import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  ResponsiveContainer, LineChart, Line, YAxis, XAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import { Scale, Ruler, Plus, TrendingDown, TrendingUp, Minus, Loader2, Percent, HelpCircle, X, Clock, Target } from 'lucide-react';
import { toast } from 'sonner';
import { MeasurementAvatar } from './MeasurementAvatar';
import { SmartTooltip } from '../../components/SmartTooltip';
import { MeasurementComparison } from './MeasurementComparison';
import { MeasurementGuideModal } from './MeasurementGuideModal';

// 8 zones essentielles (simplifié depuis les 20 zones STYKU le 15/09/2026, retour
// utilisateur : trop de mesures, avatar peu clair) + poids/masse grasse à part. Voir
// MeasurementAvatar.jsx pour le mapping visuel des repères sur la photo.
const BASE_METRICS = [
  { key: 'weight', fr: 'Poids', en: 'Weight', unit: 'kg', icon: Scale, lowerBetter: true },
  { key: 'body_fat', fr: '% Masse grasse', en: 'Body fat %', unit: '%', icon: Percent, lowerBetter: true },
];

const CORE_METRICS = [
  { key: 'chest', fr: 'Poitrine', en: 'Chest', unit: 'cm', icon: Ruler, lowerBetter: false },
  { key: 'waist', fr: 'Taille', en: 'Waist', unit: 'cm', icon: Ruler, lowerBetter: true },
  { key: 'hips', fr: 'Hanches', en: 'Hips', unit: 'cm', icon: Ruler, lowerBetter: true },
  { key: 'biceps', fr: 'Biceps', en: 'Biceps', unit: 'cm', icon: Ruler, lowerBetter: false },
  { key: 'thigh', fr: 'Cuisse', en: 'Thigh', unit: 'cm', icon: Ruler, lowerBetter: false },
];

const ADVANCED_METRICS = [
  { key: 'calf', fr: 'Mollet', en: 'Calf', unit: 'cm', icon: Ruler, lowerBetter: false },
];

const METRIC_GROUPS = [
  { title: 'Poids & masse grasse', titleEn: 'Weight & body fat', items: BASE_METRICS },
  { title: 'Mensurations essentielles', titleEn: 'Essential measurements', items: CORE_METRICS },
  { title: 'Zones avancées (optionnel)', titleEn: 'Advanced zones (optional)', items: ADVANCED_METRICS },
];

const METRICS = [...BASE_METRICS, ...CORE_METRICS, ...ADVANCED_METRICS];
const ZONE_FIELDS = [...CORE_METRICS, ...ADVANCED_METRICS, ...BASE_METRICS.filter((m) => m.key === 'body_fat')].map((m) => m.key);

const GOAL_LABELS = { weight_loss: 'Perte de gras', muscle_gain: 'Prise de muscle', maintenance: 'Maintien', recomposition: 'Recomposition' };

const todayIso = () => new Date().toLocaleDateString('en-CA');
const fmtShort = (iso) => {
  try { return new Date(iso + 'T12:00:00').toLocaleDateString('fr-CH', { day: '2-digit', month: 'short' }); }
  catch { return iso; }
};

const MetricTile = ({ m, entries, en, onSelect, active }) => {
  const pts = entries.filter((e) => e[m.key] != null).map((e) => ({ date: e.date, v: e[m.key] }));
  const has = pts.length > 0;
  const latest = has ? pts[pts.length - 1].v : null;
  const first = has ? pts[0].v : null;
  const delta = has && pts.length > 1 ? +(latest - first).toFixed(1) : null;
  const improved = delta != null && (m.lowerBetter ? delta < 0 : delta > 0);
  const DeltaIcon = delta == null || delta === 0 ? Minus : delta < 0 ? TrendingDown : TrendingUp;
  const deltaColor = delta == null || delta === 0 ? '#a1a1aa' : improved ? '#34D399' : '#fb7185';
  const clickable = m.key !== 'weight' && pts.length >= 2;
  return (
    <button
      type="button"
      disabled={!clickable}
      className={`text-left rounded-xl border p-3 transition-colors w-full ${active ? 'border-primary/60 bg-primary/5' : 'border-white/10 bg-zinc-900/60'} ${clickable ? 'hover:border-primary/40 cursor-pointer' : 'cursor-default'}`}
      data-testid={`metric-${m.key}`}
      onClick={() => clickable && onSelect && onSelect(active ? null : m.key)}
      aria-pressed={!!active}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <m.icon className={`w-3.5 h-3.5 ${has ? 'text-primary' : 'text-zinc-500'}`} />
        <span className={`text-xs ${has ? 'text-zinc-400' : 'text-zinc-500'}`}>{en ? m.en : m.fr}</span>
      </div>
      {!has ? (
        <p className="text-zinc-600 text-sm">—</p>
      ) : (
        <div className="flex items-end justify-between">
          <div>
            <p className="text-xl font-black text-white tabular-nums leading-none">{latest}<span className="text-xs text-zinc-500 font-normal ml-0.5">{m.unit}</span></p>
            {delta != null && (
              <p className="text-[11px] font-semibold flex items-center gap-0.5 mt-1" style={{ color: deltaColor }}>
                <DeltaIcon className="w-3 h-3" /> {delta > 0 ? '+' : ''}{delta} {m.unit}
              </p>
            )}
          </div>
          {pts.length > 1 && (
            <div className="w-16 h-9">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={pts}>
                  <YAxis hide domain={['dataMin', 'dataMax']} />
                  <Line type="monotone" dataKey="v" stroke={deltaColor} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </button>
  );
};

// Mini-graphique d'évolution d'une zone précise, affiché au clic sur l'avatar (ou survol d'une
// tuile) — répond au besoin "voir l'historique, pas juste la dernière valeur".
const ZoneHistoryChart = ({ metric, pts, en, onClose, goal, onSaveGoal, saving }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(goal != null ? String(goal) : '');
  useEffect(() => { setDraft(goal != null ? String(goal) : ''); }, [goal]);
  const latest = pts.length ? pts[pts.length - 1].v : null;
  const delta = (goal != null && latest != null) ? +(latest - goal).toFixed(1) : null;
  const yDomain = goal != null
    ? [(dataMin) => Math.min(dataMin, goal) - 1, (dataMax) => Math.max(dataMax, goal) + 1]
    : ['dataMin - 1', 'dataMax + 1'];
  const submit = async () => {
    const raw = draft.trim();
    const value = raw === '' ? null : parseFloat(raw.replace(',', '.'));
    if (raw !== '' && (isNaN(value) || value <= 0 || value > 400)) {
      toast.error(en ? 'Invalid goal' : 'Objectif invalide');
      return;
    }
    await onSaveGoal(metric.key, value);
    setEditing(false);
  };
  return (
    <div className="mt-4 rounded-xl bg-zinc-800/40 border border-primary/25 p-4" data-testid="zone-history-chart">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <metric.icon className="w-4 h-4 text-primary" />
          <span className="text-sm font-bold text-white">{en ? `${metric.en} history` : `Historique : ${metric.fr}`}</span>
        </div>
        <div className="flex items-center gap-2">
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold text-primary bg-primary/10 border border-primary/30 hover:bg-primary/20 transition-colors"
              data-testid="zone-goal-edit-btn"
            >
              <Target className="w-3 h-3" />
              {goal != null ? `${en ? 'Goal' : 'Objectif'}: ${goal} ${metric.unit}` : (en ? 'Set a goal' : 'Fixer un objectif')}
            </button>
          )}
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="zone-history-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      {editing && (
        <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="zone-goal-form">
          <input
            type="number" inputMode="decimal" step="0.1" autoFocus
            value={draft} onChange={(e) => setDraft(e.target.value)}
            placeholder={en ? `Target ${metric.unit}` : `Cible en ${metric.unit}`}
            className="w-32 bg-zinc-900 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:border-primary focus:outline-none"
            data-testid="zone-goal-input"
          />
          <button onClick={submit} disabled={saving}
            className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-50"
            data-testid="zone-goal-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (en ? 'Save' : 'Enregistrer')}
          </button>
          {goal != null && (
            <button onClick={() => { setDraft(''); onSaveGoal(metric.key, null).then(() => setEditing(false)); }}
              className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-zinc-300 bg-zinc-800 border border-white/10 hover:text-white"
              data-testid="zone-goal-clear-btn">
              {en ? 'Clear' : 'Retirer'}
            </button>
          )}
          <button onClick={() => setEditing(false)}
            className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-zinc-400 hover:text-white">
            {en ? 'Cancel' : 'Annuler'}
          </button>
        </div>
      )}
      {goal != null && delta != null && (
        <div className="mb-2 text-[11px] flex items-center gap-2">
          <span className="text-zinc-400">{en ? 'Distance to goal:' : "Écart à l'objectif :"}</span>
          <span className={`font-bold ${(metric.lowerBetter ? delta <= 0 : delta >= 0) ? 'text-emerald-400' : 'text-amber-400'}`}
            data-testid="zone-goal-delta">
            {delta > 0 ? '+' : ''}{delta} {metric.unit}
          </span>
        </div>
      )}
      <div className="h-32">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={pts} margin={{ top: 6, right: 10, left: -22, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="label" tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis domain={yDomain} tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#fff' }}
              labelStyle={{ color: '#a1a1aa' }} formatter={(v) => [`${v} ${metric.unit}`, en ? metric.en : metric.fr]} />
            {goal != null && (
              <ReferenceLine y={goal} stroke="#FBBF24" strokeDasharray="4 3"
                label={{ value: `${en ? 'Goal' : 'Objectif'} ${goal}`, position: 'insideTopRight', fill: '#FBBF24', fontSize: 10 }} />
            )}
            <Line type="monotone" dataKey="v" stroke="#ec4899" strokeWidth={3} dot={{ fill: '#ec4899', r: 3 }} activeDot={{ r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};


export const MeasurementsCard = ({ api, en, onChanged, onWeightAlert }) => {
  const [entries, setEntries] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  // Sexe de l'avatar : détecté automatiquement depuis le profil du client (dernier calcul
  // de calories), avec possibilité de le corriger manuellement via le toggle Femme/Homme.
  const [sex, setSex] = useState('female');
  const [sexAuto, setSexAuto] = useState(true);
  const [active, setActive] = useState(null);
  const avatarRef = useRef(null);
  const [goalHistory, setGoalHistory] = useState([]);
  const [measurementGoals, setMeasurementGoals] = useState({});
  const [savingGoal, setSavingGoal] = useState(false);

  const load = useCallback(() => {
    api('/api/tracking/weight').then((d) => setEntries(d.entries || [])).catch(() => setEntries([]));
    api('/api/tracking/profile/history').then((d) => setGoalHistory((d.history || []).filter((h) => h.field === 'goal'))).catch(() => {});
    api('/api/tracking/measurement-goals').then((d) => setMeasurementGoals(d.goals || {})).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const saveZoneGoal = useCallback(async (key, value) => {
    setSavingGoal(true);
    try {
      const res = await api('/api/tracking/measurement-goals', {
        method: 'PUT', body: JSON.stringify({ goals: { [key]: value } }),
      });
      setMeasurementGoals(res.goals || {});
      toast.success(en ? 'Goal saved' : 'Objectif enregistré');
    } catch {
      toast.error(en ? 'Save failed' : "Échec de l'enregistrement");
    } finally { setSavingGoal(false); }
  }, [api, en]);

  // Détection automatique du sexe depuis le profil (une seule fois, sans écraser un choix manuel)
  useEffect(() => {
    api('/api/tracking/profile')
      .then((d) => { if (sexAuto && d?.sex) setSex(d.sex); })
      .catch(() => {});
  }, [api, sexAuto]);

  const chooseSex = (s) => { setSexAuto(false); setSex(s); };

  const save = async () => {
    const weight = parseFloat(form.weight);
    if (isNaN(weight)) { toast.error(en ? 'Weight is required' : 'Le poids est requis'); return; }
    const date = form.date || todayIso();
    if (date > todayIso()) { toast.error(en ? 'Date cannot be in the future' : 'La date ne peut pas être dans le futur'); return; }
    const payload = { date, weight };
    ZONE_FIELDS.forEach((k) => {
      const v = parseFloat(form[k]); if (!isNaN(v)) payload[k] = v;
    });
    setSaving(true);
    try {
      const res = await api('/api/tracking/weight', { method: 'POST', body: JSON.stringify(payload) });
      toast.success(en ? 'Measurements saved' : 'Mensurations enregistrées', {
        description: res?.goals
          ? (en ? `Targets updated: ${res.goals.calories} kcal` : `Objectifs mis à jour : ${res.goals.calories} kcal`)
          : (en
            ? 'Want to see your progress? Take a before/after photo of the month.'
            : 'Envie de voir votre évolution ? Prenez votre photo avant/après du mois.'),
        duration: 8000,
        action: {
          label: en ? 'Take a photo' : 'Prendre une photo',
          onClick: () => {
            document.getElementById('progress-photos-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          },
        },
        data: { testid: 'measurement-saved-photo-nudge' },
      });
      setForm({}); setOpen(false); load();
      onWeightAlert && onWeightAlert(res?.weight_alert);
      onChanged && onChanged();
    } catch {
      toast.error(en ? 'Save failed' : "Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  if (entries === null) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const latest = {};
  METRICS.forEach((m) => {
    const pts = entries.filter((e) => e[m.key] != null);
    if (pts.length) latest[m.key] = pts[pts.length - 1][m.key];
  });
  const avatarValues = latest;
  const weightPts = entries.filter((e) => e.weight != null).map((e) => ({ date: e.date, weight: e.weight, label: fmtShort(e.date) }));
  const wDelta = weightPts.length > 1 ? +(weightPts[weightPts.length - 1].weight - weightPts[0].weight).toFixed(1) : null;
  // Rappel doux : dernière mensuration de zone (pas juste le poids) notée il y a >= au
  // seuil ADAPTATIF calculé selon les habitudes du client — s'il mesure toutes les
  // semaines, on rappelle plus tôt ; s'il mesure une fois par mois, on lui laisse le temps.
  const entriesWithZone = entries.filter((e) => ZONE_FIELDS.some((f) => e[f] != null));
  const lastEntryDate = entriesWithZone.length ? entriesWithZone[entriesWithZone.length - 1].date : null;
  const daysSinceLastEntry = lastEntryDate
    ? Math.floor((new Date(`${todayIso()}T00:00:00`) - new Date(`${lastEntryDate}T00:00:00`)) / 86400000)
    : null;
  // Cadence médiane observée (jours entre 2 relevés de zone consécutifs)
  let medianInterval = null;
  if (entriesWithZone.length >= 2) {
    const gaps = [];
    for (let i = 1; i < entriesWithZone.length; i++) {
      const gap = Math.round(
        (new Date(`${entriesWithZone[i].date}T00:00:00`) - new Date(`${entriesWithZone[i - 1].date}T00:00:00`)) / 86400000
      );
      if (gap > 0) gaps.push(gap);
    }
    if (gaps.length) {
      gaps.sort((a, b) => a - b);
      const mid = Math.floor(gaps.length / 2);
      medianInterval = gaps.length % 2 === 0 ? Math.round((gaps[mid - 1] + gaps[mid]) / 2) : gaps[mid];
    }
  }
  // Seuil = 1.4 × cadence habituelle, entre 10 et 45 jours. Fallback 14j si pas d'historique.
  const reminderThreshold = medianInterval
    ? Math.min(45, Math.max(10, Math.round(medianInterval * 1.4)))
    : 14;
  const showStaleReminder = daysSinceLastEntry != null && daysSinceLastEntry >= reminderThreshold;
  // Zone sélectionnée sur l'avatar (ou survolée sur une tuile) -> mini-historique dédié.
  // Mapping 1:1 désormais (chaque anneau 3D = une métrique unique, plus de fusion G/D).
  const activeMetric = METRICS.find((m) => m.key === active && m.key !== 'weight');
  const activePts = activeMetric
    ? entries.filter((e) => e[activeMetric.key] != null).map((e) => ({ date: e.date, v: e[activeMetric.key], label: fmtShort(e.date) }))
    : [];
  // Repères "changement d'objectif" sur la courbe : place le marqueur au 1er point de poids
  // enregistré à partir de la date du changement.
  // Dédoublonne les repères tombant sur le même point de la courbe : ne garde que le plus
  // récent changement d'objectif (goalHistory est trié du plus récent au plus ancien).
  const goalMarkersSeen = new Set();
  const goalMarkers = goalHistory
    .map((h) => {
      const changeDate = h.changed_at.slice(0, 10);
      const point = weightPts.find((p) => p.date >= changeDate);
      return point ? { x: point.label, text: GOAL_LABELS[h.new_value] || h.new_value } : null;
    })
    .filter((m) => {
      if (!m || goalMarkersSeen.has(m.x)) return false;
      goalMarkersSeen.add(m.x);
      return true;
    });

  return (
    <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-5" data-testid="measurements-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="flex items-center gap-2 font-bold text-white"><Ruler className="w-4 h-4 text-primary" /> {en ? 'Body measurements' : 'Mensurations'}</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setGuideOpen(true)}
            className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-bold text-zinc-300 bg-zinc-800 border border-white/10 hover:border-primary/40 hover:text-white transition-colors whitespace-nowrap"
            data-testid="measurement-guide-btn"
          >
            <HelpCircle className="w-3.5 h-3.5" /> {en ? 'Guide' : <><span className="hidden sm:inline">Guide de mesure</span><span className="sm:hidden">Guide</span></>}
          </button>
          <button onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-105 transition-transform whitespace-nowrap" data-testid="add-measurement-btn">
            <Plus className="w-3.5 h-3.5" /> {en ? 'Add' : 'Ajouter'}
          </button>
        </div>
      </div>

      {showStaleReminder && (
        <button
          onClick={() => { setOpen(true); }}
          className="w-full mb-5 rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-primary/10 px-4 py-3 flex items-center justify-between gap-3 hover:border-amber-500/50 transition-colors text-left"
          data-testid="measurement-reminder-banner"
        >
          <div className="flex items-center gap-2.5">
            <Clock className="w-4 h-4 text-amber-400 shrink-0" />
            <p className="text-sm text-white font-semibold">
              {en
                ? `No measurement logged in ${daysSinceLastEntry} days — take 2 minutes to update it.`
                : `Aucune mensuration notée depuis ${daysSinceLastEntry} jours — prenez 2 minutes pour la mettre à jour.`}
            </p>
          </div>
          <span className="text-xs font-bold text-black bg-gradient-to-r from-amber-400 to-primary px-3 py-1.5 rounded-full whitespace-nowrap shrink-0">{en ? 'Add' : 'Ajouter'}</span>
        </button>
      )}

      {open && (
        <div className="mb-5 rounded-xl bg-zinc-800/50 border border-white/10 p-4" data-testid="measurement-form">
          <div className="mb-3">
            <label className="text-xs text-zinc-400 mb-1 block">{en ? 'Date' : 'Date'}</label>
            <input
              type="date"
              value={form.date || todayIso()}
              max={todayIso()}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              className="w-full sm:w-48 bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
              data-testid="measure-date-input"
            />
            <p className="text-[11px] text-zinc-500 mt-1">{en ? 'Correcting a past entry overwrites that day\'s measurement.' : 'Choisissez une date passée pour corriger une mesure oubliée — cela remplace la mesure de ce jour-là.'}</p>
          </div>
          <div className="space-y-4">
            {METRIC_GROUPS.map((g) => (
              <div key={g.title}>
                <p className="text-[11px] font-bold uppercase tracking-wider text-primary/80 mb-2">{en ? g.titleEn : g.title}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {g.items.map((m) => (
                    <div key={m.key}>
                      <label className="text-xs text-zinc-400 mb-1 block">{en ? m.en : m.fr} ({m.unit}){m.key === 'weight' && ' *'}</label>
                      <input
                        type="number" inputMode="decimal" step="0.1"
                        value={form[m.key] || ''}
                        onChange={(e) => setForm((f) => ({ ...f, [m.key]: e.target.value }))}
                        className="w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
                        data-testid={`measure-input-${m.key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button onClick={save} disabled={saving} className="mt-4 w-full py-2.5 rounded-lg font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-measurement-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {en ? 'Save' : 'Enregistrer'}
          </button>
        </div>
      )}

      {/* Évolution du poids */}
      {weightPts.length >= 2 && (
        <div className="mb-5 rounded-xl bg-zinc-800/40 border border-white/10 p-4" data-testid="weight-evolution">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2"><Scale className="w-4 h-4 text-primary" /><span className="text-sm font-bold text-white">{en ? 'Weight evolution' : 'Évolution du poids'}</span></div>
            {wDelta != null && (
              <span className={`inline-flex items-center gap-1 text-sm font-bold ${wDelta <= 0 ? 'text-emerald-400' : 'text-pink-400'}`}>
                {wDelta <= 0 ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />} {wDelta > 0 ? '+' : ''}{wDelta} kg
              </span>
            )}
          </div>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weightPts} margin={{ top: 14, right: 30, left: -22, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="label" tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis domain={['dataMin - 1', 'dataMax + 1']} tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, color: '#fff' }} labelStyle={{ color: '#a1a1aa' }} />
                {goalMarkers.map((m, i) => {
                  const idx = weightPts.findIndex((p) => p.label === m.x);
                  const nearEnd = idx >= weightPts.length - 2;
                  return (
                    <ReferenceLine key={i} x={m.x} stroke="#FBBF24" strokeDasharray="4 3"
                      label={(props) => (
                        <text x={props.viewBox.x} y={10} textAnchor={nearEnd ? 'end' : 'start'} fill="#FBBF24" fontSize={10}>
                          {m.text}
                        </text>
                      )} />
                  );
                })}
                <Line type="monotone" dataKey="weight" stroke="#E91E63" strokeWidth={3} dot={{ fill: '#E91E63', r: 3 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Avatar + tuiles */}
      <div className="grid md:grid-cols-2 gap-5 items-start">
        <div ref={avatarRef} className="rounded-xl bg-gradient-to-b from-zinc-800/30 to-transparent border border-white/10 p-4">
          <div className="flex items-center justify-center gap-1.5 mb-2" data-testid="avatar-sex-toggle">
            {[{ k: 'female', fr: 'Femme', en: 'Female' }, { k: 'male', fr: 'Homme', en: 'Male' }].map((s) => (
              <button key={s.k} onClick={() => chooseSex(s.k)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${sex === s.k ? 'bg-gradient-to-r from-primary to-pink-500 text-white' : 'bg-zinc-800 text-zinc-400 border border-white/10'}`}
                data-testid={`avatar-sex-${s.k}`}>
                {en ? s.en : s.fr}
              </button>
            ))}
          </div>
          <p className="text-center text-[11px] text-zinc-500 mb-1">{en ? 'Where to measure (tap a highlighted zone)' : 'Où prendre vos mensurations (touchez une zone en surbrillance)'}</p>
          <MeasurementAvatar
            measurements={avatarValues}
            onSelect={(zoneKey) => setActive(zoneKey)}
            selectedField={active}
            gender={sex === 'male' ? 'male' : 'female'}
            en={en}
          />
        </div>

        <div className="space-y-4">
          {METRIC_GROUPS.map((g) => (
            <div key={g.title}>
              <p className="text-[11px] font-bold uppercase tracking-wider text-primary/80 mb-2">{en ? g.titleEn : g.title}</p>
              <div className="grid grid-cols-2 gap-3">
                {g.items.map((m) => <MetricTile key={m.key} m={m} entries={entries} en={en} onSelect={setActive} active={active === m.key} />)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {activeMetric && activePts.length >= 2 && (
        <ZoneHistoryChart
          metric={activeMetric} pts={activePts} en={en}
          onClose={() => setActive(null)}
          goal={measurementGoals[activeMetric.key] ?? null}
          onSaveGoal={saveZoneGoal}
          saving={savingGoal}
        />
      )}

      {entries.length === 0 && (
        <p className="text-center text-zinc-500 text-sm mt-4">
          {en ? 'Add your first measurement to see your avatar come to life.' : 'Ajoutez votre première mesure pour voir votre avatar prendre vie.'}
        </p>
      )}

      {entries.length >= 2 ? (
        <MeasurementComparison entries={entries} en={en} />
      ) : entries.length === 1 ? (
        <p className="text-center text-zinc-500 text-xs mt-5 rounded-xl bg-zinc-800/40 border border-white/10 py-3 px-4" data-testid="comparison-locked-hint">
          {en
            ? 'Add a second measurement (in a few weeks) to unlock your progress comparison & analysis.'
            : 'Ajoutez une 2ème mensuration (dans quelques semaines) pour débloquer la comparaison et l\'analyse de votre progression.'}
        </p>
      ) : null}

      <SmartTooltip
        id="measurements-avatar"
        title={{ fr: 'Votre avatar de mensurations', en: 'Your measurement avatar' }}
        description={{ fr: "Touchez une zone en surbrillance (poitrine, taille, hanches...) pour voir précisément où mesurer et son évolution en détail. Choisissez aussi votre silhouette homme/femme.", en: 'Tap a highlighted zone (chest, waist, hips...) to see exactly where to measure and its detailed evolution. You can also pick your male/female silhouette.' }}
        targetRef={avatarRef}
        show={entries !== null}
        en={en}
      />
      <MeasurementGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} en={en} />
    </div>
  );
};

export default MeasurementsCard;
